import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, clearAuth, readCachedUser, writeCachedUser } from "@/lib/api";
import {
    buildLoginPayload,
    createActivityReporter,
    SESSION_ACTIVITY_EVENTS,
} from "@/lib/sessionPersistence";

const AuthContext = createContext(null);

/**
 * Auth provider.
 *
 * The actual session is held server-side via an httpOnly cookie. Locally we
 * only cache the *public* user profile (name/email/role/tenant) so the SPA
 * can paint the shell on reload without an extra round-trip.
 *
 * On boot, we hydrate from the cache for instant UI, then `refreshMe()`
 * fires in the background. If the cookie is missing/expired, /auth/me will
 * 401 and the axios interceptor sends the user to /login.
 */
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(readCachedUser);
    const [loading, setLoading] = useState(false);

    const login = useCallback(async (email, password, keepSignedIn = false) => {
        setLoading(true);
        try {
            const { data } = await api.post(
                "/auth/login",
                buildLoginPayload(email, password, keepSignedIn),
            );
            // The httpOnly cookie is set by the backend on the same response.
            writeCachedUser(data.user);
            setUser(data.user);
            return data.user;
        } finally {
            setLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (err) {
            // Logout should never block the UX even if the backend is unreachable.
            console.warn("auth: logout request failed", err?.message || err);
        }
        clearAuth();
        setUser(null);
    }, []);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            writeCachedUser(data);
            setUser(data);
        } catch (err) {
            // 401 is the typical case (cookie expired/missing); the axios
            // interceptor handles the redirect to /login. We log other status
            // codes for visibility without surfacing a toast.
            if (err?.response?.status && err.response.status !== 401) {
                console.warn("auth: refreshMe failed", err.response.status, err.message);
            }
        }
    }, []);

    // Hydrate session from server on first mount (so we know the cookie is valid).
    useEffect(() => {
        if (user) refreshMe();
        // We intentionally run this only once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Polling y requests automáticos no deben mantener viva una sesión normal.
    // Solo interacciones humanas explícitas informan actividad, como máximo una vez
    // cada cinco minutos; el backend aplica el mismo límite y el vencimiento absoluto.
    useEffect(() => {
        if (!user) return undefined;
        const reportActivity = createActivityReporter(
            () => api.post("/auth/activity"),
        );
        const reportVisibleActivity = () => {
            if (document.visibilityState === "visible") reportActivity();
        };
        SESSION_ACTIVITY_EVENTS.forEach((eventName) => document.addEventListener(eventName, reportActivity, { passive: true }));
        document.addEventListener("visibilitychange", reportVisibleActivity);
        return () => {
            SESSION_ACTIVITY_EVENTS.forEach((eventName) => document.removeEventListener(eventName, reportActivity));
            document.removeEventListener("visibilitychange", reportVisibleActivity);
        };
    }, [user]);

    const value = useMemo(
        () => ({ user, loading, login, logout, refreshMe }),
        [user, loading, login, logout, refreshMe],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
